// src/App.js — Operations Dashboard (Admin + Canteen Staff)
import { useState, useEffect, useCallback } from 'react'
import { supabase } from './lib/supabaseClient'

// Page Imports
import LoginPage from './pages/LoginPage'
import OverviewTab from './pages/OverviewTab'
import OrdersTab from './pages/OrdersTab'
import TicketsTab from './pages/TicketsTab'
import { WebhooksTab, RefundsTab } from './pages/WebhooksAndRefundsTab'
import CanteenDashboard from './pages/CanteenDashboard' // 👈 Staff kitchen view

// Icons
import { LayoutDashboard, ShoppingBag, MessageSquare, AlertTriangle, RotateCcw, LogOut } from 'lucide-react'

const TABS = [
  { id: 'overview',  label: 'Overview',  Icon: LayoutDashboard },
  { id: 'orders',    label: 'Orders',    Icon: ShoppingBag },
  { id: 'tickets',   label: 'Tickets',   Icon: MessageSquare },
  { id: 'webhooks',  label: 'Webhooks',  Icon: AlertTriangle },
  { id: 'refunds',   label: 'Refunds',   Icon: RotateCcw },
]

export default function App() {
  const [session, setSession]   = useState(null)
  const [loading, setLoading]   = useState(true)
  const [userRole, setUserRole] = useState(null) // 'admin' | 'staff' | null
  const [tab, setTab]           = useState('overview')

  const verifyUserAccess = useCallback(async (currentSession) => {
    if (!currentSession) {
      setUserRole(null)
      setSession(null)
      setLoading(false)
      return
    }

    setSession(currentSession)

    // 1. Check if it's Canteen Staff (No MFA required for kitchen iPads)
    const role = currentSession.user.user_metadata?.role
    if (role === 'canteen_staff') {
      setUserRole('staff')
      setLoading(false)
      return
    }

    // 2. Check if it's an Admin (Strict MFA required)
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (aalData?.currentLevel === 'aal2') {
      const { data: profile } = await supabase
        .from('admin_profiles')
        .select('user_id')
        .eq('user_id', currentSession.user.id)
        .single()
        
      if (profile) {
        setUserRole('admin')
        setLoading(false)
        return
      }
    }

    // 3. Fallback: Logged in, but unauthorized for this portal
    setUserRole(null)
    setLoading(false)
  }, [])

  useEffect(() => {
    // Initial check on mount
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      verifyUserAccess(s)
    })

    // Listen for login/logout/MFA verification events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_IN' || event === 'MFA_CHALLENGE_VERIFIED') {
        verifyUserAccess(s)
      } else if (event === 'SIGNED_OUT') {
        setUserRole(null)
        setSession(null)
      }
    })
    
    return () => subscription.unsubscribe()
  }, [verifyUserAccess])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setUserRole(null)
    setSession(null)
  }

  // ── RENDER: Loading State ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1c1c1e] border-t-orange-500 rounded-full animate-spin shadow-[0_0_15px_rgba(249,115,22,0.3)]" />
      </div>
    )
  }

  // ── RENDER: Unauthenticated ────────────────────────────────────────────────
  if (!userRole) {
    return <LoginPage onLogin={(s) => verifyUserAccess(s)} />
  }

  // ── RENDER: Canteen Staff UI ───────────────────────────────────────────────
  if (userRole === 'staff') {
    return (
      <div className="min-h-screen bg-[#121212]">
        {/* Render the full-screen kitchen dashboard */}
        <CanteenDashboard />
        
        {/* Floating Emergency Logout for Staff iPads */}
        <button 
          onClick={handleLogout}
          className="fixed bottom-6 right-6 bg-[#1c1c1e] text-red-400 p-4 rounded-full shadow-2xl border border-white/5 active:scale-90 transition-transform"
          title="Sign Out"
        >
          <LogOut className="w-6 h-6" />
        </button>
      </div>
    )
  }

  // ── RENDER: Super Admin UI ─────────────────────────────────────────────────
  const renderAdminTab = () => {
    switch (tab) {
      case 'overview':  return <OverviewTab onTabSwitch={setTab} />
      case 'orders':    return <OrdersTab />
      case 'tickets':   return <TicketsTab />
      case 'webhooks':  return <WebhooksTab />
      case 'refunds':   return <RefundsTab />
      default:          return <OverviewTab onTabSwitch={setTab} />
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100 font-sans">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-52 bg-[#111111] border-r border-white/5 flex flex-col z-40">
        <div className="p-5 border-b border-white/5">
          <p className="text-orange-500 font-bold text-sm tracking-wide">OrderQ</p>
          <p className="text-gray-600 text-[10px] uppercase tracking-widest mt-0.5">Operations</p>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === id
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.03]'
              }`}>
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-white/5">
          <div className="px-3 py-2 mb-1">
            <p className="text-gray-500 text-[10px] uppercase tracking-widest">Signed in as Admin</p>
            <p className="text-gray-300 text-xs mt-0.5 truncate">{session?.user?.email}</p>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-all">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-52 p-6 min-h-screen">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-white font-semibold text-lg capitalize">{tab}</h1>
            <p className="text-gray-600 text-xs">{new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
          </div>
          {renderAdminTab()}
        </div>
      </div>
    </div>
  )
}